import { useMemo, useState } from "react";
import { toast } from "../../utils/toast";
import { Button, Input, Modal, Select } from "../ui";
import { useDraft } from "../../hooks/useDraft";
import { errorMessageOf, fieldErrorsOf, problemOf, scholarshipsApi } from "../../api/scholarships";
import { COUNTRY_OPTIONS, useEducationLevels } from "./model";

const formOf = (programme, countryCode) => ({
  name: programme?.name ?? "",
  institutionName: programme?.institutionName ?? "",
  countryCode: programme?.countryCode ?? countryCode ?? "",
  levelId: programme?.level?.id ?? "",
  durationMonths: programme?.durationMonths ? String(programme.durationMonths) : "",
});

const validate = (form, withPlace) => {
  const errors = {};
  if (!form.name.trim()) errors.name = "Programme name is required";
  if (withPlace && !form.institutionName.trim()) errors.institutionName = "University or institution is required";
  if (withPlace && !form.countryCode) errors.countryCode = "Country is required";
  if (!form.levelId) errors.levelId = "Education level is required";
  const months = Number(form.durationMonths);
  if (!form.durationMonths) errors.durationMonths = "Duration is required";
  else if (!Number.isInteger(months) || months < 1 || months > 120) errors.durationMonths = "Enter a whole number of months from 1 to 120";
  return errors;
};

// Programmes are shared by every call: one already in the catalogue (same name, institution,
// country and level) answers 409 programme-exists with its ID, and is offered as it is
export const createProgramme = async (body) => {
  try {
    const { data } = await scholarshipsApi.post("admin/programmes", body);
    return { id: data.id, existing: false };
  } catch (error) {
    const problem = problemOf(error);
    if (problem?.code === "programme-exists" && problem.body.programmeId) return { id: problem.body.programmeId, existing: true };
    throw error;
  }
};

/**
 * Creates a programme for a scholarship, or edits one (`programme` set). `onSaved` gets the saved
 * programme (only its ID and name when created). Closing by clicking out keeps the form as a draft; Cancel and a successful save clear it.
 * Editing calls PATCH /admin/programmes/{id}. Without `withPlace` (abroad loans) a programme is a
 * study level: the student names the university and the country.
 */
export default function ProgrammeDialog({ open, programme, countryCode, withPlace = true, onClose, onSaved }) {
  const editing = Boolean(programme);
  const initial = useMemo(() => formOf(programme, countryCode), [programme, countryCode]);
  const [form, setForm, clearForm] = useDraft(editing ? `programme-edit:${programme.id}` : "programme-new", initial);
  const [errors, setErrors] = useState({});
  const [saving, setSaving] = useState(false);

  const { data: levels = [], isError: levelsFailed } = useEducationLevels({ keep: [programme?.level] });
  const levelOptions = useMemo(() => levels.map((l) => ({ value: l.id, label: l.levelName })), [levels]);

  const update = (e) => {
    const { name, value } = e.target;
    setForm((prev) => ({ ...prev, [name]: value }));
    if (errors[name]) setErrors((prev) => ({ ...prev, [name]: undefined }));
  };

  const cancel = () => {
    clearForm();
    setErrors({});
    onClose();
  };

  const save = async (addAnother) => {
    const found = validate(form, withPlace);
    setErrors(found);
    if (Object.keys(found).length) return;

    const body = {
      name: form.name.trim(),
      ...(withPlace && { institutionName: form.institutionName.trim(), countryCode: form.countryCode }),
      levelId: form.levelId,
      durationMonths: Number(form.durationMonths),
    };
    setSaving(true);
    try {
      let message;
      if (editing) {
        const { data } = await scholarshipsApi.patch(`admin/programmes/${programme.id}`, body);
        await onSaved(data);
        message = `${data.name} updated`;
      } else {
        const created = await createProgramme(body);
        await onSaved({ id: created.id, name: body.name });
        message = created.existing ? `${body.name} is already in the catalogue: added it from there` : `${body.name} added`;
      }
      toast.success(message);
      clearForm();
      setErrors({});
      if (addAnother) setForm(formOf(null, form.countryCode));
      else onClose();
    } catch (error) {
      console.error(`Failed to ${editing ? "update" : "create"} programme:`, error);
      setErrors(fieldErrorsOf(problemOf(error)));
      toast.error(errorMessageOf(error, "Failed to save the programme. Please try again."));
    } finally {
      setSaving(false);
    }
  };

  return (
    <Modal
      open={open}
      onClose={onClose}
      dismissible={!saving}
      size="lg"
      title={editing ? "Edit programme" : "New programme"}
      footer={
        <>
          <Button variant="ghost" onClick={cancel} disabled={saving}>
            Cancel
          </Button>
          {!editing && (
            <Button variant="outline" onClick={() => save(true)} disabled={saving}>
              Add and add another
            </Button>
          )}
          <Button onClick={() => save(false)} loading={saving}>
            {editing ? "Save changes" : "Add programme"}
          </Button>
        </>
      }
    >
      <form
        onSubmit={(e) => {
          e.preventDefault();
          save(false);
        }}
        noValidate
        className="grid grid-cols-1 gap-5 md:grid-cols-2"
      >
        <Input label="Programme name" name="name" required maxLength={200} placeholder={withPlace ? "e.g. MSc Computer Science" : "e.g. Masters"} value={form.name} onChange={update} error={errors.name} className="md:col-span-2" />
        {withPlace && (
          <>
            <Input
              label="University or institution"
              name="institutionName"
              required
              maxLength={200}
              placeholder="e.g. Tsinghua University"
              value={form.institutionName}
              onChange={update}
              error={errors.institutionName}
              className="md:col-span-2"
            />
            <Select label="Country" name="countryCode" required placeholder="Select a country" value={form.countryCode} onChange={update} options={COUNTRY_OPTIONS} error={errors.countryCode} />
          </>
        )}
        <Select
          label="Education level"
          name="levelId"
          required
          placeholder={levelsFailed ? "Could not load education levels" : "Select a level"}
          value={form.levelId}
          onChange={update}
          options={levelOptions}
          error={errors.levelId}
        />
        <Input type="number" min="1" max="120" step="1" label="Duration (months)" name="durationMonths" required placeholder="e.g. 24" value={form.durationMonths} onChange={update} error={errors.durationMonths} />
      </form>
    </Modal>
  );
}
